import {useEffect, useState} from "react";
import {
    Badge,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow
} from "@fluentui/react-components";
import {isScheduleItemFinished} from "../handlers/schedulehandler";
import {getFormatForTimeValue, formatDateForTitle} from "../handlers/datehandler";
import {getParticipantString, getTitleString} from "./ScheduleItemForView";

const formatElapsedTime = (startTime) => {
    const diffMs = Math.max(0, Date.now() - new Date(startTime));
    const hours = Math.floor(diffMs / 3_600_000);
    const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
    const seconds = Math.floor((diffMs % 60_000) / 1_000);
    return `${getFormatForTimeValue(hours)}:${getFormatForTimeValue(minutes)}:${getFormatForTimeValue(seconds)}`;
};

const formatCountdown = (targetTime) => {
    const diffMs = new Date(targetTime) - Date.now();
    if (diffMs <= 0) return null;
    const hours = Math.floor(diffMs / 3_600_000);
    const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
    const seconds = Math.floor((diffMs % 60_000) / 1_000);
    return `${getFormatForTimeValue(hours)}:${getFormatForTimeValue(minutes)}:${getFormatForTimeValue(seconds)}`;
};

const getFinishTimeString = (scheduleItem, finishTime) => {
    const diffMs = new Date(finishTime) - new Date(scheduleItem.race_start_time);
    if (diffMs < 0) return `-`;
    const hours = Math.floor(diffMs / 3_600_000);
    const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
    const seconds = Math.floor((diffMs % 60_000) / 1_000);
    const milliseconds = diffMs % 1_000;
    return `${getFormatForTimeValue(hours)}:${getFormatForTimeValue(minutes)}:${getFormatForTimeValue(seconds)}.${milliseconds.toString().padStart(3, "0")}`;
};

const PODIUM_MEDALS = ["🥇", "🥈", "🥉"];

const LiveScheduleCard = ({scheduleItem, animationDelay = 0, broadcast = false}) => {
    const isFinished = isScheduleItemFinished(scheduleItem);
    const isStarted = Boolean(scheduleItem.race_start_time) && !isFinished;

    const [elapsedTime, setElapsedTime] = useState(() =>
        isStarted ? formatElapsedTime(scheduleItem.race_start_time) : "00:00:00"
    );
    const [countdown, setCountdown] = useState(() =>
        scheduleItem.planned_start_time ? formatCountdown(scheduleItem.planned_start_time) : null
    );

    useEffect(() => {
        if (!isStarted) return;
        const interval = setInterval(() => {
            setElapsedTime(formatElapsedTime(scheduleItem.race_start_time));
        }, 1000);
        return () => clearInterval(interval);
    }, [isStarted, scheduleItem.race_start_time]);

    useEffect(() => {
        if (isStarted || isFinished || !scheduleItem.planned_start_time) return;
        const interval = setInterval(() => {
            setCountdown(formatCountdown(scheduleItem.planned_start_time));
        }, 1000);
        return () => clearInterval(interval);
    }, [isStarted, isFinished, scheduleItem.planned_start_time]);

    const getParticipantsByStartNR = (startNR) => {
        const found = scheduleItem.participants.find(
            (p) => parseInt(p.start_nr) === parseInt(startNR)
        );
        return found ? getParticipantString(found.participants) : "";
    };

    const cardClass = isFinished
        ? "live-card live-card--finished"
        : isStarted
            ? "live-card live-card--live"
            : "live-card live-card--upcoming";

    const raceStateLabel = isFinished
        ? "Gefinished"
        : isStarted
            ? "Gestart"
            : "Aankomend";

    const raceStateColor = isFinished
        ? "success"
        : isStarted
            ? "warning"
            : "informative";

    const raceStateDescription = isFinished
        ? `Resultaat van race ${scheduleItem.race_nr}`
        : isStarted
            ? `Gestart om ${formatDateForTitle(scheduleItem.race_start_time)}`
            : `Gepland om ${formatDateForTitle(scheduleItem.planned_start_time)}`;

    return (
        <div className={`${cardClass} ${broadcast ? `live-card--broadcast` : ``}`} style={{animationDelay: `${animationDelay}ms`}}>
            {/* Header */}
            <div className={`flex flex-gap-medium flex-align-center flex-wrap live-card-header`}>
                <Badge appearance="filled" color={raceStateColor}>{raceStateLabel}</Badge>
                <h3 className={`live-card-title`} style={broadcast ? {fontSize: "1.7rem", lineHeight: 1.1} : {}}>{getTitleString(scheduleItem)}</h3>
            </div>

            {broadcast && (
                <div className={`flex flex-column flex-gap-small`}>
                    <span className={`text-small text-sub`}>{raceStateDescription}</span>
                </div>
            )}

            {/* Live elapsed timer */}
            {isStarted && (
                <div className={`live-timer-container`}>
                    <span className={`live-timer-label`}>Looptijd</span>
                    <span className={`live-timer`} style={broadcast ? {fontSize: "46px"} : {}}>{elapsedTime}</span>
                </div>
            )}

            {/* Upcoming countdown */}
            {!isStarted && !isFinished && scheduleItem.planned_start_time && (
                <div className={`live-countdown-container`}>
                    {countdown &&
                        <>
                            <span className={`live-timer-label`}>Start over</span>
                            <span className={`live-countdown`} style={broadcast ? {fontSize: "34px"} : {}}>{countdown}</span>
                        </>
                    }
                </div>
            )}

            {/* Results table */}
            {isFinished && (
                <Table style={{tableLayout: "fixed"}}>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell style={{width: 60}}><b>Positie</b></TableHeaderCell>
                            <TableHeaderCell style={{width: 130}}><b>Tijd</b></TableHeaderCell>
                            <TableHeaderCell><b>Deelnemers</b></TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {scheduleItem.results.map((result, index) => (
                            <TableRow
                                key={index}
                                className={index < 3 ? `live-podium-row live-podium-row--${index}` : ``}
                            >
                                <TableCell>
                                    <span className={`live-position`}>
                                        {index < 3 ? PODIUM_MEDALS[index] : index + 1}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    {isNaN(result.order)
                                        ? result.order
                                        : getFinishTimeString(scheduleItem, result.finish_time)}
                                </TableCell>
                                <TableCell>{getParticipantsByStartNR(result.start_nr)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            {/* Participants table (not yet finished) */}
            {!isFinished && (
                <Table style={{tableLayout: "fixed"}}>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell style={{width: 60}}><b>Startnr.</b></TableHeaderCell>
                            <TableHeaderCell><b>Deelnemers</b></TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {scheduleItem.participants.map((participant, index) => (
                            <TableRow key={index}>
                                <TableCell>{participant.start_nr}</TableCell>
                                <TableCell>{getParticipantString(participant.participants)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    );
};

export default LiveScheduleCard;

